import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import MetaTags from "react-meta-tags";
import axios from "axios";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  ButtonGroup,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import { sampleReport } from "store/databaseofunits/actions";
import { getStockList } from "store/scheme/actions";

const API_BASE = "https://externalqcapi.com/api";

// ─── Helpers ────────────────────────────────────────────────────────────────

// Coerces API values to real booleans ("false" string is NOT truthy here).
const toBool = v => v === true || v === "true" || v === 1 || v === "1";

// True for values that carry no information (missing or the "-" placeholder).
const isEmptyVal = v => v === undefined || v === null || v === "" || v === "-";

// Merges several records of the same participant into one. The first
// non-empty value for each field wins, so a record with the address
// fills the gaps left by one without it.
const mergeRecords = records =>
  records.reduce((acc, rec) => {
    Object.keys(rec || {}).forEach(key => {
      if (isEmptyVal(acc[key]) && !isEmptyVal(rec[key])) {
        acc[key] = rec[key];
      }
    });

    return acc;
  }, {});

// Opens a URL in a new TAB (window.open with a features string can make
// some browsers open a separate popup window instead).
const openInNewTab = href => {
  const a = document.createElement("a");
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

// Lets the Sample Dispatch Report (another tab) update instantly.
const DC_CHANNEL = "dc_status_channel";

const broadcastDcStatus = (participantId, field, value) => {
  try {
    const ch = new BroadcastChannel(DC_CHANNEL);

    ch.postMessage({ participantId: String(participantId), field, value });
    ch.close();
  } catch (e) {
    // BroadcastChannel unsupported - report page will refetch on focus instead
  }
};

// DC numbers are auto-generated as YYMM-001 — a fresh sequence starts
// each month (YYMM changes → counter resets to 001), persisted in
// localStorage so refreshing this page doesn't hand out a different
// number for the same challan.
//
// ✅ UNIQUENESS: a DC No is NEVER reused. Every number that is shown or
// saved is checked against ALL DC numbers that exist on the server (every
// lab, not only the current one) plus this browser's counter, and the
// next free number is taken instead when there is a clash.
const DC_COUNTER_KEY = "delivery_challan_counter";

const monthStamp = () => {
  const d = new Date();

  return `${String(d.getFullYear()).slice(-2)}${String(
    d.getMonth() + 1
  ).padStart(2, "0")}`;
};

// Today's date as yyyy-mm-dd in the user's LOCAL time zone (toISOString
// uses UTC, which gives yesterday's date early in the morning in Pakistan).
const todayStr = () => {
  const d = new Date();

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const readDcStore = () => {
  let store = {};

  try {
    store = JSON.parse(localStorage.getItem(DC_COUNTER_KEY) || "{}");
  } catch (e) {
    store = {};
  }

  const stamp = monthStamp();

  // Reset the running sequence whenever the month changes.
  if (store.stamp !== stamp) {
    store = { stamp, seq: 0, issued: {} };
  }

  store.issued = store.issued || {};

  return store;
};

const writeDcStore = store => {
  try {
    localStorage.setItem(DC_COUNTER_KEY, JSON.stringify(store));
  } catch (e) {
    // storage unavailable - numbers are still checked against the server
  }
};

// Pass a stable key (e.g. the participant id) so navigating to this page
// twice for the same participant/dispatch reuses the same DC No instead
// of burning a new number, while a different participant still gets a new
// one.
const getOrCreateDcNo = key => {
  const store = readDcStore();
  const stamp = store.stamp;
  const cacheKey = key || "unknown";

  if (store.issued[cacheKey]) {
    return store.issued[cacheKey];
  }

  store.seq = (store.seq || 0) + 1;

  const dcNo = `${stamp}-${String(store.seq).padStart(3, "0")}`;

  store.issued[cacheKey] = dcNo;

  writeDcStore(store);

  return dcNo;
};

// Repeat DC mode is selected with ?mode=repeat in the URL.
const isRepeatMode = search =>
  new URLSearchParams(search || "").get("mode") === "repeat";

// Next free DC No in this month's sequence, skipping every number in `used`
// (all DC numbers already on the server) and every number this browser has
// already handed out.
// commit=false  → only PREVIEWS the number (nothing is saved, no number is burned)
// commit=true   → reserves it in localStorage
const getNextDcNo = (used = new Set(), commit = false) => {
  const store = readDcStore();
  const stamp = store.stamp;

  const issuedHere = new Set(Object.values(store.issued || {}));

  let seq = store.seq || 0;
  let dcNo = "";

  do {
    seq += 1;
    dcNo = `${stamp}-${String(seq).padStart(3, "0")}`;
  } while ((used.has(dcNo) || issuedHere.has(dcNo)) && seq < 9999);

  if (commit) {
    store.seq = seq;
    writeDcStore(store);
  }

  return dcNo;
};

// Marks a DC No as taken in this browser (called after it was really saved),
// so the counter never goes back to it.
const reserveDcNo = dcNo => {
  const store = readDcStore();
  const match = /^(\d{4})-(\d+)$/.exec(String(dcNo || ""));

  if (!match || match[1] !== store.stamp) return;

  store.seq = Math.max(store.seq || 0, Number(match[2]));
  writeDcStore(store);
};

const normalize = s =>
  typeof s === "string"
    ? s.toLowerCase().trim()
    : String(s ?? "")
        .toLowerCase()
        .trim();

// "571a-0403, 571a-0404" → ["571a-0403", "571a-0404"] (normalized)
const splitRoundNames = str =>
  String(str || "")
    .split(",")
    .map(r => normalize(r))
    .filter(Boolean);

// Reads the payment date from a participant record. Accepts a few possible
// field names and trims datetimes ("2026-10-05T00:00:00Z") down to
// yyyy-mm-dd so the Order No / Order Date always render cleanly.
const getPayDate = p => {
  const raw = p?.paydate || p?.pay_date || p?.payment_date || "";

  if (!raw || raw === "-") return "";

  return String(raw).slice(0, 10);
};

// Builds the Order No (NHS-YYMMDD) from a yyyy-mm-dd pay date.
const buildOrderNo = paydate =>
  paydate ? `NHS-${paydate.replace(/-/g, "").slice(2)}` : "-";

// A round only counts as "open for dispatch" when its status is Open AND
// it's flagged open_for "Both" or "Customer Service".
const isRoundOpenForDispatch = round => {
  if (round?.status?.toLowerCase() !== "open") {
    return false;
  }

  const of = normalize(round?.open_for || "");

  return of === "both" || of === "customer_service";
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// ✅ The ONE date format used everywhere on the frontend: DD-MMM-YY
// (e.g. "05-Oct-26"). Parsed manually from the yyyy-mm-dd text (no Date
// object) so time zones can never shift the day.
const formatDMY = value => {
  if (!value || value === "-") return "-";

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));

  if (!match) return String(value);

  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1];

  if (!monthName) return String(value);

  return `${day}-${monthName}-${year.slice(-2)}`;
};

// Kept as aliases so every date on the page uses the same format.
const formatDateForPrint = formatDMY;
const formatExpiryDate = formatDMY;

// Date field that ALWAYS shows DD-MMM-YY. The visible box is read-only text;
// a transparent native date input sits on top of it, so clicking the box
// opens the browser's date picker. The value stays yyyy-mm-dd internally.
const DateField = ({ value, onChange, width, className }) => (
  <div style={{ position: "relative", width }} className={className}>
    <Input
      bsSize="sm"
      type="text"
      readOnly
      value={value ? formatDMY(value) : ""}
      placeholder="DD-MMM-YY"
      style={{ cursor: "pointer", backgroundColor: "#fff" }}
    />
    <input
      type="date"
      value={value || ""}
      onChange={onChange}
      onClick={e => {
        try {
          if (e.target.showPicker) e.target.showPicker();
        } catch (err) {
          // picker not available - the native control still works
        }
      }}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        opacity: 0,
        cursor: "pointer",
      }}
    />
  </div>
);

DateField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  width: PropTypes.string,
  className: PropTypes.string,
};

DateField.defaultProps = {
  value: "",
  width: "170px",
  className: "",
};

// ─── Print stylesheet ───────────────────────────────────────────────────────
const PRINT_STYLES = `
  @media print {
    /* Hide the app layout (top bar, sidebar, breadcrumb/page title, footer)
       so nothing prints over the challan */
    #page-topbar,
    header,
    .navbar-header,
    .vertical-menu,
    .page-title-box,
    .breadcrumb,
    .footer {
      display: none !important;
    }

    /* Remove the space the layout reserves for the sidebar */
    .main-content {
      margin-left: 0 !important;
      padding: 0 !important;
    }

    .page-content {
      padding: 10mm 0 0 0 !important; /* pushes everything down a little */
      margin: 0 !important;
    }
    .card {
      border: none !important;
      box-shadow: none !important;
    }
    .d-print-none {
      display: none !important;
    }
    .print-only-value {
      display: inline-block !important;
    }

    /* Logo: only visible when printing, aligned to the right at the top */
    .print-logo {
      display: block !important;
      position: relative !important;
      z-index: 9999 !important;
      text-align: right !important;
      margin: 0 0 6mm 0 !important;
    }
    .print-logo img {
      display: inline-block !important;
      height: 80px !important;
    }

    /* Each saved DC is its own challan page */
    .challan-block {
      page-break-after: always;
      break-after: page;
    }
    .challan-block:last-child {
      page-break-after: auto;
      break-after: auto;
    }

    .challan-front-page {
      border: 1px solid #000 !important;
      padding: 12px 16px 8px 16px !important;
      margin-bottom: 14px !important;
      font-size: 15px !important;
      display: flex !important;
      flex-wrap: nowrap !important;
    }
    /* Details = left half of the box */
    .challan-front-page .challan-details-col {
      flex: 0 0 50% !important;
      max-width: 50% !important;
    }
    /* Customer Service = right half only (never goes past the middle),
       text right-aligned, long address wraps onto the next line */
    .challan-front-page .challan-address-col {
      flex: 0 0 50% !important;
      max-width: 50% !important;
      text-align: right !important;
      overflow-wrap: anywhere !important;
      word-break: break-word !important;
    }
    /* Address block: tighter line spacing */
    .challan-front-page .challan-address-col > div:last-child {
      line-height: 1.4 !important;
      margin: 0 !important;
      padding: 0 !important;
      text-align: right !important;
    }
    .challan-front-page .challan-details-col .mb-2 {
      margin-bottom: 6px !important;
    }
    .challan-front-page .challan-address-col,
    .challan-front-page .challan-details-col {
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
  }

  /* Customer details: right-aligned (screen + print) */
  .challan-address-col {
    text-align: right;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  /* "Details" / "Customer Service" headings */
  .challan-section-heading {
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .print-only-value {
    display: none;
  }
  .print-logo {
    display: none; /* hidden on screen, shown only in print */
  }

  /* Front page box (Details + Customer Services): larger text on screen */
  .challan-front-page {
    font-size: 16px;
  }

  /* Page title shown right below the logo */
  .challan-title {
    text-align: left;
    font-weight: 700;
    font-size: 26px;
    margin: 0 0 16px 0;
  }

  /* Scheme table: roomier rows + all data bold (screen and print) */
.challan-scheme-table {
  table-layout: fixed;
  width: 100%;
}
.challan-scheme-table th,
.challan-scheme-table td {
  padding: 16px 10px !important;
  vertical-align: middle !important;
  white-space: normal !important;
  overflow-wrap: anywhere !important;
  word-break: break-word !important;
}
.challan-scheme-table th {
  font-size: 16px;
  font-weight: 600;
}
.challan-scheme-table tbody tr {
  height: 60px;
}
.challan-scheme-table td,
.challan-scheme-table td * {
  font-weight: 700 !important;
}

  /* Electronic document note at the end of the page */
  .challan-note {
    margin-top: 18px;
    font-size: 13px;
    font-style: italic;
    text-align: center;
  }

  @page {
    size: A4;
    margin: 15mm;
  }
`;

// ─── Component ──────────────────────────────────────────────────────────────

class DeliveryChallan extends Component {
  constructor(props) {
    super(props);

    const participantFromState = props.location?.state?.participant || null;

    const roundPeriodFilterFromState =
      props.location?.state?.roundPeriodFilter || null;

    const isRepeat = isRepeatMode(props.location?.search);

    this.state = {
      isRepeat,

      // Existing DC lines for this participant (used by Repeat DC table
      // AND to re-show saved data on a normal, already-created DC).
      history: [],
      historyLoading: false,
      historyError: "",
      showAllRounds: false,
      repeatCreated: false,

      // ✅ Every DC No that exists on the server, for ALL labs. Used to make
      // sure a new DC No is never a duplicate.
      allDcNos: [],

      // "all"     = saved challans shown read-only (schemes without a DC
      //             show as "Pending")
      // "pending" = saved challans in a read-only table + a SEPARATE table
      //             to create a NEW challan only for the schemes/rounds
      //             that don't have one yet
      viewMode: "all",

      // Which saved DC block is currently being printed (null = print all).
      // Every other block is hidden from the print while this is set.
      printDcNo: null,

      // Normal mode only: the saved DC loaded from the server.
      // savedLines is keyed by scheme_name → { lot_no, expiry_date, quantity }.
      savedLines: {},
      savedDcNo: "",

      participant: participantFromState,

      // "All" / null means no round-period restriction.
      roundPeriodFilter: roundPeriodFilterFromState || "All",

      dcNo:
        participantFromState && !isRepeat
          ? getOrCreateDcNo(
              participantFromState.id || participantFromState.participant_id
            )
          : "",

      // Defaults to TODAY (local date). Shown as DD-MMM-YY.
      dispatchDate: todayStr(),

      // Lot No / Expiry Date / Quantity per scheme row. Lot No / Expiry
      // Date are only stored here when the user has picked a lot from the
      // dropdown for a scheme with multiple lots.
      schemeFieldsById: {},

      // Order No placeholder
      orderNo: "",

      // Pay date is coming from Payment.paydate. Seeded from the participant
      // passed in via router state (if it carries one) and refreshed from
      // the sample report once that loads.
      paydate: getPayDate(participantFromState),

      // True once a DC exists for this participant - hides the Create DC
      // button so a duplicate can't be created.
      dcCreated: toBool(participantFromState?.dc_created),

      // Tracks the Create DC button's in-flight/feedback state.
      creatingDc: false,
      dcCreateMessage: "",
    };
  }

  componentDidMount() {
    const { SampleReport } = this.props;

    const authUser = localStorage.getItem("authUser");
    const user_id = authUser ? JSON.parse(authUser).user_id : "";

    if (user_id) {
      // Stock list feeds the per-scheme Lot No / Expiry Date auto-fill.
      this.props.onGetStock(user_id);
    }

    // Load the DCs already created for this participant (both modes):
    // Repeat mode shows them in a table, normal mode re-fills the saved DC.
    this.fetchDcHistory();

    // Load every DC No on the server so new numbers are always unique.
    this.fetchAllDcNos();

    // Hydrate immediately from whatever is already in redux (avoids a
    // flicker)...
    if (Array.isArray(SampleReport) && SampleReport.length) {
      this.tryHydrateFromSampleReport(SampleReport);
    }

    // ...but ALWAYS fetch a fresh copy too. The cached copy may be stale or
    // may not include paydate, and previously the API was skipped entirely
    // whenever the cache was non-empty.
    if (user_id) {
      this.props.onGetSampleReport(user_id);
    } else {
      console.error(
        "❌ user_id missing in localStorage 'authUser' — sample report API not called."
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.SampleReport !== this.props.SampleReport &&
      Array.isArray(this.props.SampleReport)
    ) {
      this.tryHydrateFromSampleReport(this.props.SampleReport);
    }

    // ✅ If the page is in "Pending Only" view but no scheme is pending any
    // more (all schemes have a DC), go back to the normal view so the
    // pending option / button is never shown for a fully-covered lab.
    if (
      !this.state.isRepeat &&
      this.state.viewMode === "pending" &&
      !this.state.historyLoading &&
      !this.state.creatingDc &&
      this.state.history.length > 0 &&
      Array.isArray(this.props.SampleReport) &&
      this.props.SampleReport.length > 0 &&
      this.getPendingSchemes().length === 0
    ) {
      this.handleViewModeChange("all");
    }

    // Participant became known (or changed) after mount → load its DCs.
    const idOf = p => p?.id ?? p?.participant_id;

    if (
      idOf(this.state.participant) &&
      idOf(prevState.participant) !== idOf(this.state.participant)
    ) {
      this.fetchDcHistory();
    }
  }

  // Finds EVERY record of this participant in the sample report (both the
  // payment-level participants list and each round's participants list),
  // merges them into one so name / address / attention / city are filled
  // from whichever record actually has them, and then merges that into the
  // participant already on screen. Real values already on screen win, but
  // a "-" placeholder never blocks a real value.
  //
  // This is also what makes "Open in New Page" work: a fresh tab has no
  // router state, so the participant is rebuilt from the ?participant_id=
  // (and ?round_period=) query params in the URL.
  tryHydrateFromSampleReport = SampleReport => {
    const params = new URLSearchParams(this.props.location.search);

    const participantIdFromUrl = params.get("participant_id");

    const currentParticipant = this.state.participant;

    const participantId =
      participantIdFromUrl ||
      currentParticipant?.id ||
      currentParticipant?.participant_id;

    if (this.state.roundPeriodFilter === "All" && params.get("round_period")) {
      this.setState({
        roundPeriodFilter: params.get("round_period"),
      });
    }

    if (!participantId || !Array.isArray(SampleReport)) {
      return;
    }

    const pid = participantId.toString();

    let paydate = getPayDate(currentParticipant);
    let dcCreated = false;

    // Collect EVERY record for this participant (not just the first one).
    const allRecords = [];

    for (const scheme of SampleReport) {
      // Payment-level records are the same source the report page uses
      // for the DC Created checkbox.
      (scheme.participants || [])
        .filter(p => p.id?.toString() === pid)
        .forEach(p => {
          if (toBool(p.dc_created)) dcCreated = true;
        });

      const candidates = [
        ...(scheme.participants || []),
        ...(scheme.round_details || []).flatMap(r => r.participants || []),
      ].filter(p => p.id?.toString() === pid);

      candidates.forEach(c => {
        allRecords.push(c);
        paydate = paydate || getPayDate(c);
      });
    }

    if (allRecords.length) {
      // Combine all records so name, address, attention, city etc. are
      // filled in from whichever record actually has them.
      const mergedFromReport = mergeRecords(allRecords);

      this.setState(prev => ({
        // Values already on screen win, but only if they're real values
        // (a "-" placeholder never blocks a real one). paydate goes last.
        participant: {
          ...mergeRecords([currentParticipant, mergedFromReport]),
          id: mergedFromReport.id ?? currentParticipant?.id ?? pid,
          paydate,
        },

        // In repeat mode the DC No is previewed live (see getCurrentDcNo).
        // In normal mode a DC No loaded from the server always wins.
        dcNo: prev.isRepeat
          ? prev.dcNo
          : prev.savedDcNo ||
            getOrCreateDcNo(
              mergedFromReport.id || mergedFromReport.participant_id || pid
            ),

        paydate,

        dcCreated: prev.dcCreated || dcCreated,
      }));
    } else if (paydate && paydate !== this.state.paydate) {
      this.setState({ paydate });
    }
  };

  // ── Unique DC numbers ─────────────────────────────────────────────────────

  // Loads every DC No that exists on the server (all labs) and keeps it in
  // state. Returns the list so callers can use it immediately (setState is
  // asynchronous). Returns null if the numbers could not be loaded.
  fetchAllDcNos = async () => {
    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : "";

    if (!token) return null;

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Token ${token}`,
    };

    const load = async url => {
      const response = await fetch(url, { headers });
      const data = await response.json();

      if (!response.ok) throw new Error("Could not load DC numbers");

      return Array.isArray(data) ? data : [];
    };

    try {
      let lines = await load(
        `${API_BASE}/database-admin/delivery-challans/?summary=1`
      );

      // The lightweight summary may not carry dc_no - fall back to the full list.
      if (lines.length && lines.every(l => !l.dc_no)) {
        lines = await load(`${API_BASE}/database-admin/delivery-challans/`);
      }

      const nos = [...new Set(lines.map(l => l.dc_no).filter(Boolean))];

      this.setState({ allDcNos: nos });

      return nos;
    } catch (err) {
      console.error("❌ Failed to load all DC numbers:", err);

      return null;
    }
  };

  // ── Saved / Repeat / Pending DC ───────────────────────────────────────────

  // Loads every DC line already created for this participant.
  // Returns a promise so callers can `await` it.
  fetchDcHistory = async () => {
    const params = new URLSearchParams(this.props.location.search);
    const p = this.state.participant;
    const pid = params.get("participant_id") || p?.id || p?.participant_id;

    if (!pid) return;

    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : "";

    this.setState({ historyLoading: true, historyError: "" });

    try {
      const response = await fetch(
        `${API_BASE}/database-admin/delivery-challans/?participant=${encodeURIComponent(
          pid
        )}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || data.error || "Could not load delivery challans."
        );
      }

      const list = Array.isArray(data) ? data : [];

      // Safety net: if the API returns a participant field, only keep this
      // participant's lines even if the server ignored the ?participant= filter.
      const mine = list.filter(item => {
        const ip = item.participant ?? item.participant_id;
        const ipId = ip && typeof ip === "object" ? ip.id : ip;

        return (
          ipId === undefined || ipId === null || String(ipId) === String(pid)
        );
      });

      this.setState({ history: mine, historyLoading: false });

      // Normal mode: re-show the saved DC data on the page.
      if (!this.state.isRepeat) {
        this.applySavedDc(mine);
      }
    } catch (err) {
      this.setState({
        historyLoading: false,
        historyError: err.message || "Could not load delivery challans.",
      });
    }
  };

  // Normal mode: takes the participant's saved DC lines (the original DC =
  // earliest date / DC No), and puts its DC No, date, lot, expiry and
  // quantity back on screen so the page opens with the already-saved data.
  applySavedDc = lines => {
    if (!Array.isArray(lines) || !lines.length) return;

    const first = [...lines].sort(
      (a, b) =>
        String(a.dispatch_date).localeCompare(String(b.dispatch_date)) ||
        String(a.dc_no).localeCompare(String(b.dc_no))
    )[0];

    const savedLines = {};

    lines
      .filter(item => item.dc_no === first.dc_no)
      .forEach(item => {
        savedLines[item.scheme_name] = {
          lot_no: item.lot_no || "",
          expiry_date: item.expiry_date || "",
          quantity:
            item.quantity === undefined || item.quantity === null
              ? "1"
              : String(item.quantity),
        };
      });

    this.setState({
      savedLines,
      savedDcNo: first.dc_no,
      dcNo: first.dc_no,
      dispatchDate: first.dispatch_date
        ? String(first.dispatch_date).slice(0, 10)
        : this.state.dispatchDate,
      dcCreated: true,
    });
  };

  // True when the page is creating a NEW challan (the Repeat page, or the
  // "Pending Only" view).
  isNewDcMode = () => this.state.isRepeat || this.state.viewMode === "pending";

  // Open scheme rows, cut down to the rounds that have NO challan yet.
  // A scheme with rounds "A, B" where only A has a DC comes back as "B".
  getPendingSchemes = () => {
    const covered = new Set();

    this.state.history.forEach(item => {
      splitRoundNames(item.rounds).forEach(r =>
        covered.add(`${normalize(item.scheme_name)}|${r}`)
      );
    });

    const pending = [];

    this.getParticipantSchemes().forEach(scheme => {
      const missing = String(scheme.rounds || "")
        .split(",")
        .map(r => r.trim())
        .filter(Boolean)
        .filter(
          r => !covered.has(`${normalize(scheme.scheme_name)}|${normalize(r)}`)
        );

      if (missing.length) {
        pending.push({ ...scheme, rounds: missing.join(", ") });
      }
    });

    return pending;
  };

  // Rows shown in the (editable) scheme table.
  getDisplayedSchemes = () =>
    !this.state.isRepeat && this.state.viewMode === "pending"
      ? this.getPendingSchemes()
      : this.getParticipantSchemes();

  // Open schemes that already have a saved DC (shown in the read-only table
  // above the pending table).
  getSavedSchemes = () =>
    this.getParticipantSchemes().filter(row => this.findSavedLine(row));

  // Newest saved DC line that covers this scheme row (same scheme + at
  // least one same round). DCs of old cycles never match a new round.
  findSavedLine = row => {
    const rowRounds = splitRoundNames(row.rounds);

    return (
      this.state.history
        .filter(
          item =>
            normalize(item.scheme_name) === normalize(row.scheme_name) &&
            splitRoundNames(item.rounds).some(r => rowRounds.includes(r))
        )
        .sort(
          (a, b) =>
            String(b.dispatch_date).localeCompare(String(a.dispatch_date)) ||
            String(b.dc_no).localeCompare(String(a.dc_no))
        )[0] || null
    );
  };

  // The saved line for a scheme row, or null when the table is in
  // "new DC" mode (asNew = true). A row with no matching DC comes back as
  // { missing: true }.
  getSavedLine = (row, asNew = this.isNewDcMode()) => {
    if (asNew) return null;

    const line = this.findSavedLine(row);

    if (line) {
      return {
        dc_no: line.dc_no,
        dispatch_date: line.dispatch_date,
        lot_no: line.lot_no || "",
        expiry_date: line.expiry_date || "",
        quantity:
          line.quantity === undefined || line.quantity === null
            ? "1"
            : String(line.quantity),
      };
    }

    if (this.state.history.length) {
      return {
        missing: true,
        lot_no: "",
        expiry_date: "",
        quantity: "-",
      };
    }

    return null;
  };

  // DC Nos + dates of the saved challans that cover the open schemes.
  getSavedSummary = () => {
    const nos = new Set();
    const dates = new Set();

    this.getParticipantSchemes().forEach(row => {
      const line = this.findSavedLine(row);

      if (line) {
        nos.add(line.dc_no);
        dates.add(String(line.dispatch_date).slice(0, 10));
      }
    });

    return { dcNos: [...nos], dates: [...dates].sort() };
  };

  // Switches between "All Schemes" and "Pending Only".
  handleViewModeChange = mode => {
    if (mode === this.state.viewMode) return;

    this.setState(
      {
        viewMode: mode,
        schemeFieldsById: {},
        dcCreateMessage: "",
        repeatCreated: false,
      },
      () => {
        if (mode === "pending") {
          this.setState({ dispatchDate: todayStr() });
          // make sure the preview number is checked against the latest list
          this.fetchAllDcNos();
        } else {
          this.applySavedDc(this.state.history);
        }
      }
    );
  };

  // Existing DC lines shown in the history table. By default only the DCs
  // of the participant's current open scheme/round(s); the checkbox shows all.
  // A line matches when the scheme name is the same AND at least one round
  // overlaps (saved "571a-0403, 571a-0404" still matches open "571a-0404").
  // If nothing matches the open rounds, all saved DCs are shown instead of
  // an empty table.
  getVisibleHistory = () => {
    const { history, showAllRounds } = this.state;

    let rows = [...history];

    if (!showAllRounds) {
      const current = this.getParticipantSchemes();

      if (current.length) {
        const matching = rows.filter(item => {
          const itemRounds = splitRoundNames(item.rounds);

          return current.some(
            s =>
              normalize(s.scheme_name) === normalize(item.scheme_name) &&
              splitRoundNames(s.rounds).some(r => itemRounds.includes(r))
          );
        });

        if (matching.length) rows = matching;
      }
    }

    return rows
      .sort(
        (a, b) =>
          String(b.dispatch_date).localeCompare(String(a.dispatch_date)) ||
          String(b.dc_no).localeCompare(String(a.dc_no))
      )
      .map((item, idx) => ({
        ...item,
        _key: `${item.dc_no}-${item.id ?? idx}`,
      }));
  };

  // ✅ Saved DCs grouped by DC No (oldest first). Each group becomes its own
  // challan block with its own header + its own print. Only lines that
  // belong to the participant's current open scheme/rounds are used.
  getSavedGroups = () => {
    const groups = {};

    this.getVisibleHistory().forEach(item => {
      const g = (groups[item.dc_no] = groups[item.dc_no] || {
        dcNo: item.dc_no,
        date: String(item.dispatch_date || "").slice(0, 10),
        rows: [],
      });

      g.rows.push({
        id: `${item.dc_no}|${item.scheme_name}`,
        scheme_name: item.scheme_name,
        rounds: item.rounds,
        lot_no: item.lot_no || "",
        expiry_date: item.expiry_date || "",
        quantity:
          item.quantity === undefined || item.quantity === null
            ? "1"
            : String(item.quantity),
      });
    });

    return Object.values(groups).sort(
      (a, b) =>
        String(a.date).localeCompare(String(b.date)) ||
        String(a.dcNo).localeCompare(String(b.dcNo))
    );
  };

  // The newest saved DC No (the last block).
  getLatestDcNo = () => {
    const groups = this.getSavedGroups();

    return groups.length ? groups[groups.length - 1].dcNo : null;
  };

  // ✅ Every DC No known to be taken: ALL labs on the server + this lab's
  // saved lines. `extra` lets callers add freshly fetched numbers.
  getUsedDcNos = (extra = []) => {
    const used = new Set(this.state.allDcNos);

    this.state.history.forEach(item => {
      if (item.dc_no) used.add(item.dc_no);
    });

    (extra || []).forEach(n => n && used.add(n));

    return used;
  };

  // The DC No shown on screen. It is always a number that is NOT used yet:
  // - Repeat page + Pending Only view: live PREVIEW of the next free number
  //   until the DC is created.
  // - Normal first-time DC: the number reserved for this lab, replaced by the
  //   next free one if somebody else already took it.
  getCurrentDcNo = () => {
    const { repeatCreated, dcNo, dcCreated } = this.state;

    const used = this.getUsedDcNos();

    if (this.isNewDcMode()) {
      if (repeatCreated) return dcNo;

      return getNextDcNo(used, false);
    }

    if (!dcCreated && (!dcNo || used.has(dcNo))) {
      return getNextDcNo(used, false);
    }

    return dcNo;
  };

  // True when this participant already has a DC on the chosen dispatch date
  // (only enforced on the Repeat page).
  isDateTaken = () => {
    const { history, dispatchDate } = this.state;

    return history.some(
      item => String(item.dispatch_date).slice(0, 10) === dispatchDate
    );
  };

  // After a repeat DC is created: start a fresh blank one.
  handleStartAnotherDc = () => {
    this.setState({
      repeatCreated: false,
      dcNo: "",
      dispatchDate: todayStr(),
      schemeFieldsById: {},
      dcCreateMessage: "",
    });
  };

  resetPrintData = () => {
    this.setState({
      dispatchDate: todayStr(),
      schemeFieldsById: {},
    });
  };

  // ✅ Flags the participant as "DC Printing" done using the same endpoint
  // the checkbox on the Sample Dispatch Report uses. That page then shows
  // the box checked and (together with DC Created) turns the row green.
  markDcPrinting = async () => {
    const { participant } = this.state;

    if (!participant) return;

    const authUser = localStorage.getItem("authUser");
    const userId = authUser ? JSON.parse(authUser).user_id : "";
    const participantId = participant.id || participant.participant_id;

    try {
      await axios.post(
        `${API_BASE}/registration-admin/participant/${participantId}/dc-status/`,
        {
          field: "dc_printing",
          value: true,
          added_by: userId,
        }
      );

      // Tell the Sample Dispatch Report tab right away.
      broadcastDcStatus(participantId, "dc_printing", true);

      // Refresh the shared report data so nothing stale is left in redux.
      if (userId) this.props.onGetSampleReport(userId);

      this.setState({
        dcCreateMessage: "DC Printing marked successfully.",
      });
    } catch (err) {
      console.error("❌ Failed to mark DC Printing:", err);

      this.setState({
        dcCreateMessage:
          "Could not mark DC Printing. Please tick it on the Sample Dispatch Report.",
      });
    }
  };

  // Marks DC Printing after the print dialog closes (not when Print is
  // clicked). Note: browsers fire `afterprint` whether the user printed or
  // cancelled.
  // In the saved view with several DCs this prints ALL of them, each on its
  // own page (see .challan-block page breaks).
  handlePrint = () => {
    const onAfterPrint = () => {
      window.removeEventListener("afterprint", onAfterPrint);
      this.markDcPrinting();
    };

    window.addEventListener("afterprint", onAfterPrint);
    window.print();
  };

  // ✅ Prints ONLY one saved DC block (every other block is hidden while the
  // print dialog is open).
  handlePrintDc = dcNo => {
    this.setState({ printDcNo: dcNo }, () => {
      const onAfterPrint = () => {
        window.removeEventListener("afterprint", onAfterPrint);
        this.setState({ printDcNo: null });

        // Creating a pending DC resets "DC Printing", so only printing the
        // newest DC marks it done. Reprinting an older DC changes nothing.
        if (dcNo === this.getLatestDcNo()) this.markDcPrinting();
      };

      window.addEventListener("afterprint", onAfterPrint);
      window.print();
    });
  };

  // ✅ Opens this same challan in a brand-new browser TAB. A new tab has no
  // router state, so the participant id (and round period) travel in the
  // URL instead — tryHydrateFromSampleReport rebuilds the page from them.
  handleOpenInNewPage = () => {
    const { participant, roundPeriodFilter } = this.state;

    if (!participant) return;

    const participantId = participant.id || participant.participant_id;

    const params = new URLSearchParams();

    params.set("participant_id", participantId);

    if (roundPeriodFilter && roundPeriodFilter !== "All") {
      params.set("round_period", roundPeriodFilter);
    }

    const href = this.props.history.createHref({
      pathname: this.props.location.pathname,
      search: `?${params.toString()}`,
    });

    openInNewTab(href);
  };

  // Stock entries whose scheme name matches this scheme row's name.
  // StockList entries come back with a read-only "name" field sourced
  // from the related Scheme (see StockSerializer), so matching on name
  // keeps this independent of whatever numeric id each list uses.
  getStockForScheme = schemeName => {
    const { StockList } = this.props;

    if (!Array.isArray(StockList) || !schemeName) return [];

    return StockList.filter(
      stock => (stock.name || stock.scheme_name) === schemeName
    );
  };

  // Resolves what Lot No / Expiry Date / remaining stock quantity should
  // actually show for a row:
  // - a user-picked lot (from the dropdown) always wins
  // - otherwise, if there's exactly one stock entry for the scheme, use it
  // - otherwise there's nothing to show (multiple lots, none picked yet,
  //   or no stock at all)
  getEffectiveLotFields = (row, stocksForScheme) => {
    const stateFields = this.state.schemeFieldsById[row.id] || {};

    if (stateFields.lot_no) {
      const matched = stocksForScheme.find(
        s => s.lot_no === stateFields.lot_no
      );

      return {
        lot_no: stateFields.lot_no,
        expiry_date: stateFields.expiry_date || "",
        stock_quantity: matched ? matched.quantity : null,
      };
    }

    if (stocksForScheme.length === 1) {
      return {
        lot_no: stocksForScheme[0].lot_no,
        expiry_date: stocksForScheme[0].expiry_date,
        stock_quantity: stocksForScheme[0].quantity,
      };
    }

    return { lot_no: "", expiry_date: "", stock_quantity: null };
  };

  // Called when the user picks a lot from the dropdown (scheme has more
  // than one stock entry) — sets both lot_no and its matching expiry_date
  // together so they never fall out of sync.
  handleLotSelect = (schemeId, lotNo, stocksForScheme) => {
    const matched = stocksForScheme.find(s => s.lot_no === lotNo);

    this.setState(prev => ({
      schemeFieldsById: {
        ...prev.schemeFieldsById,
        [schemeId]: {
          ...prev.schemeFieldsById[schemeId],
          lot_no: lotNo,
          expiry_date: matched ? matched.expiry_date : "",
        },
      },
    }));
  };

  // Builds the payload for the DC record from current form state and
  // POSTs it straight to the Django endpoint — no redux action/saga needed
  // for a single fire-and-forget create like this.
  //
  // Three situations:
  //  1. Normal first-time DC          → all open schemes, flags DC Created
  //  2. Repeat page (?mode=repeat)    → all open schemes again on a new date
  //  3. "Pending Only" view           → ONLY the schemes/rounds that have no
  //                                     challan yet; DC Printing is reset
  //                                     because the new challan isn't
  //                                     printed yet
  //
  // ✅ In ALL three the DC No is re-checked against every DC on the server
  // right before saving, so it is always unique.
  handleCreateDc = async () => {
    const { participant, dispatchDate, isRepeat, repeatCreated, viewMode } =
      this.state;

    const isPendingDc = !isRepeat && viewMode === "pending";
    const newDc = isRepeat || isPendingDc;

    // Never allow a duplicate DC once one exists.
    if (!participant || (newDc ? repeatCreated : this.state.dcCreated)) {
      return;
    }

    if (isRepeat && this.isDateTaken()) {
      this.setState({
        dcCreateMessage:
          "A delivery challan already exists for this date. Please choose a new date.",
      });

      return;
    }

    // Exactly the rows on screen (pending-only rows in Pending view).
    const rowsToSave = this.getDisplayedSchemes();

    if (!rowsToSave.length) {
      this.setState({
        dcCreateMessage: "There are no schemes to create a challan for.",
      });

      return;
    }

    const authUser = localStorage.getItem("authUser");
    const token = authUser ? JSON.parse(authUser).token : "";
    const userId = authUser ? JSON.parse(authUser).user_id : "";
    const participantId = participant.id || participant.participant_id;

    this.setState({ creatingDc: true, dcCreateMessage: "" });

    // ✅ Get the freshest list of used DC numbers, then pick a number that
    // is not in it.
    const freshNos = await this.fetchAllDcNos();
    const used = this.getUsedDcNos(freshNos || []);

    let finalDcNo = newDc ? this.getCurrentDcNo() : this.state.dcNo;

    if (!finalDcNo || used.has(finalDcNo)) {
      finalDcNo = getNextDcNo(used, false);
    }

    const schemes = rowsToSave.map(scheme => {
      const stocksForScheme = this.getStockForScheme(scheme.scheme_name);
      const effective = this.getEffectiveLotFields(scheme, stocksForScheme);

      return {
        scheme_name: scheme.scheme_name,
        rounds: scheme.rounds,
        lot_no: effective.lot_no,
        // Sent to the API as the raw yyyy-mm-dd value; DD-MMM-YY is
        // display-only.
        expiry_date: effective.expiry_date,
        quantity: this.state.schemeFieldsById[scheme.id]?.quantity ?? "1",
      };
    });

    const payload = {
      dc_no: finalDcNo,
      dispatch_date: dispatchDate,
      participant: participantId,
      schemes,
    };

    try {
      const response = await fetch(
        `${API_BASE}/database-admin/delivery-challans/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        const message = JSON.stringify(data.detail || data.error || data || "");

        const err = new Error(data.detail || data.error || "Request failed.");

        // The server refused the number (somebody saved it a moment ago).
        err.isDuplicateDcNo = /dc[_ ]?no|unique|already|exist|duplicate/i.test(
          message
        );

        throw err;
      }

      // The number is really used now → this browser never hands it out again.
      reserveDcNo(finalDcNo);

      // Keep the unique-number list up to date.
      this.fetchAllDcNos();

      // ── Repeat page / Pending Only view ────────────────────────────────
      if (newDc) {
        if (userId) this.props.onGetStock(userId);

        if (isPendingDc) {
          // The lab already has "DC Created" ticked, but this new challan
          // has not been printed yet → un-tick DC Printing so the report
          // shows it as "created, not printed" again.
          try {
            await axios.post(
              `${API_BASE}/registration-admin/participant/${participantId}/dc-status/`,
              {
                field: "dc_printing",
                value: false,
                added_by: userId,
              }
            );

            broadcastDcStatus(participantId, "dc_printing", false);
          } catch (statusErr) {
            console.error("❌ Failed to reset DC Printing:", statusErr);
          }

          if (userId) this.props.onGetSampleReport(userId);

          // Wait for the saved DCs to reload before switching views, so the
          // new rows never flash as "Pending".
          await this.fetchDcHistory();

          this.setState({
            creatingDc: false,
            viewMode: "all",
            repeatCreated: false,
            schemeFieldsById: {},
            dcCreated: true,
            dcCreateMessage: `Delivery challan ${finalDcNo} for the pending scheme(s) created successfully.`,
          });

          return;
        }

        // Repeat page (unchanged behaviour): reload saved DCs so the new DC
        // appears in the "Already created DC(s)" table.
        this.fetchDcHistory();

        this.setState({
          creatingDc: false,
          repeatCreated: true,
          dcNo: finalDcNo,
          dcCreateMessage: `Repeat delivery challan ${finalDcNo} created successfully.`,
        });

        return;
      }

      // ── Normal first-time DC ───────────────────────────────────────────
      // ✅ Flag the participant as "DC Created" using the same endpoint the
      // checkbox on the Sample Dispatch Report uses, so that page shows the
      // box checked and the row colored as soon as you go back to it.
      let statusSaved = true;

      // The backend just deducted stock for this DC, so refresh the stock
      // list to keep the Remaining Quantity column accurate.
      if (userId) this.props.onGetStock(userId);

      try {
        await axios.post(
          `${API_BASE}/registration-admin/participant/${participantId}/dc-status/`,
          {
            field: "dc_created",
            value: true,
            added_by: userId,
          }
        );

        // Tell the Sample Dispatch Report tab right away (checkbox + red).
        broadcastDcStatus(participantId, "dc_created", true);

        // Refresh the shared report data so nothing stale is left in redux.
        if (userId) this.props.onGetSampleReport(userId);
      } catch (statusErr) {
        console.error("❌ Failed to mark DC Created:", statusErr);
        statusSaved = false;
      }

      // Reload what was just saved so the page shows the stored data
      // (DC No, date, lot, expiry, quantity) exactly as the server has it.
      this.fetchDcHistory();

      this.setState({
        creatingDc: false,
        dcNo: finalDcNo,
        // The DC record exists, so never allow a duplicate.
        dcCreated: true,
        dcCreateMessage: statusSaved
          ? `Delivery challan ${finalDcNo} created successfully.`
          : "Delivery challan created successfully, but DC Created could not be marked. Please tick it on the Sample Dispatch Report.",
      });
    } catch (err) {
      if (err.isDuplicateDcNo) {
        // Load the latest numbers so the screen already shows a new free one.
        await this.fetchAllDcNos();

        this.setState({
          creatingDc: false,
          dcNo: newDc ? this.state.dcNo : "",
          dcCreateMessage:
            "That DC No was just taken by another challan. A new unique DC No has been assigned - please click Create DC again.",
        });

        return;
      }

      this.setState({
        creatingDc: false,
        dcCreateMessage: err.message || "Could not create delivery challan.",
      });
    }
  };

  // Schemes this participant is enrolled in,
  // restricted to their OPEN rounds only.
  getParticipantSchemes = () => {
    const { participant, roundPeriodFilter } = this.state;

    const { SampleReport } = this.props;

    if (!participant) return [];

    const pid = (participant.id ?? participant.participant_id)?.toString();

    if (!pid) return [];

    const schemes = [];

    (SampleReport || []).forEach(scheme => {
      const openRoundsForParticipant = (scheme.round_details || [])
        .filter(round => isRoundOpenForDispatch(round))
        .filter(round =>
          (round.participants || []).some(p => p.id?.toString() === pid)
        )
        .filter(
          round =>
            roundPeriodFilter === "All" ||
            normalize(round.round_period || "") === normalize(roundPeriodFilter)
        )
        .map(round => round.rounds)
        .filter(Boolean);

      if (openRoundsForParticipant.length) {
        schemes.push({
          id: scheme.name,
          scheme_name: scheme.name,
          rounds: openRoundsForParticipant.join(", "),
        });
      }
    });

    return schemes;
  };

  handleSchemeFieldChange = (schemeId, field, value) => {
    this.setState(prev => ({
      schemeFieldsById: {
        ...prev.schemeFieldsById,
        [schemeId]: {
          ...prev.schemeFieldsById[schemeId],
          [field]: value,
        },
      },
    }));
  };

  // ── Scheme table columns ──────────────────────────────────────────────────
  // Used for the normal challan, the Repeat page AND the Pending Only view.
  // asNew = true  → editable "new DC" table (Lot, Qty inputs, new DC No)
  // asNew = false → read-only table showing the saved DC data
  // DC No + Date columns are added whenever the page is creating a new DC, or
  // when the saved view has several DCs / some pending schemes:
  // DC No | Date | Scheme Name | Round(s) | Lot No | Expiry Date | Qty
  // (plus the on-screen-only Remaining Quantity column).
  buildSchemeColumns = (asNew = this.isNewDcMode()) => {
    const { dispatchDate, history } = this.state;
    const dcNo = this.getCurrentDcNo();
    const newDc = asNew;

    const savedSummary = newDc ? null : this.getSavedSummary();

    const compact =
      newDc ||
      (history.length > 0 &&
        (savedSummary.dcNos.length > 1 || this.getPendingSchemes().length > 0));

    const w = (normalWidth, compactWidth) =>
      compact ? compactWidth : normalWidth;

    const dcNoColumn = {
      dataField: "dc_no",
      text: "DC No",
      isDummyField: true,
      headerStyle: { width: "11%", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (cell, row) => {
        const saved = this.getSavedLine(row, newDc);

        if (saved) {
          return saved.missing ? (
            <span className="text-danger">Pending</span>
          ) : (
            <span>{saved.dc_no}</span>
          );
        }

        return <span>{dcNo || "-"}</span>;
      },
    };

    const dateColumn = {
      dataField: "dispatch_date",
      text: "Date",
      isDummyField: true,
      headerStyle: { width: "11%", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (cell, row) => {
        const saved = this.getSavedLine(row, newDc);

        if (saved) {
          return (
            <span>{saved.missing ? "-" : formatDMY(saved.dispatch_date)}</span>
          );
        }

        return <span>{formatDMY(dispatchDate)}</span>;
      },
    };

    const baseColumns = [
      {
        dataField: "scheme_name",
        text: "Scheme Name",
        headerStyle: { width: w("30%", "22%"), textAlign: "left" },
        style: { textAlign: "left" },
      },

      {
        dataField: "rounds",
        text: "Round",
        headerStyle: { width: w("12%", "11%"), textAlign: "center" },
        style: { textAlign: "center" },
      },

      {
        dataField: "lot_no",
        text: "Lot No",
        headerStyle: { width: w("16%", "13%"), textAlign: "center" },
        style: { textAlign: "center", padding: "4px" },
        formatter: (cell, row) => {
          // Saved DC → show the stored lot.
          const saved = this.getSavedLine(row, newDc);

          if (saved) {
            return <span>{saved.lot_no || "-"}</span>;
          }

          const stocksForScheme = this.getStockForScheme(row.scheme_name);
          const effective = this.getEffectiveLotFields(row, stocksForScheme);

          // More than one lot in stock → the user has to pick one.
          if (stocksForScheme.length > 1) {
            return (
              <>
                <Input
                  type="select"
                  bsSize="sm"
                  className="d-print-none"
                  value={effective.lot_no}
                  onChange={e =>
                    this.handleLotSelect(
                      row.id,
                      e.target.value,
                      stocksForScheme
                    )
                  }
                >
                  <option value="">Select Lot</option>
                  {stocksForScheme.map(stock => (
                    <option key={stock.id} value={stock.lot_no}>
                      {stock.lot_no}
                    </option>
                  ))}
                </Input>
                <span className="print-only-value">
                  {effective.lot_no || "-"}
                </span>
              </>
            );
          }

          // Exactly one lot in stock → plain text.
          if (stocksForScheme.length === 1) {
            return <span>{effective.lot_no || "-"}</span>;
          }

          // No stock for this scheme.
          return "-";
        },
      },

      {
        dataField: "expiry_date",
        text: "Expiry Date",
        headerStyle: { width: w("14%", "11%"), textAlign: "center" },
        style: { textAlign: "center", padding: "4px" },
        formatter: (cell, row) => {
          // Saved DC → show the stored expiry.
          const saved = this.getSavedLine(row, newDc);

          if (saved) {
            return <span>{formatDMY(saved.expiry_date)}</span>;
          }

          const stocksForScheme = this.getStockForScheme(row.scheme_name);
          const effective = this.getEffectiveLotFields(row, stocksForScheme);

          // More than one lot and none picked yet.
          if (stocksForScheme.length > 1 && !effective.lot_no) {
            return (
              <>
                <span className="text-muted d-print-none">
                  Select lot first
                </span>
                <span className="print-only-value">-</span>
              </>
            );
          }

          // Expiry is derived from the lot, so it is plain text (DD-MMM-YY).
          if (stocksForScheme.length > 0) {
            return <span>{formatDMY(effective.expiry_date)}</span>;
          }

          // No stock for this scheme.
          return "-";
        },
      },

      {
        dataField: "remaining_quantity",
        text: "Remaining Quantity",
        isDummyField: true,
        // Internal stock figure: shown on screen only, not printed.
        classes: "d-print-none",
        headerClasses: "d-print-none",
        headerStyle: { width: w("14%", "11%"), textAlign: "center" },
        style: { textAlign: "center", padding: "4px" },
        formatter: (cell, row) => {
          const stocksForScheme = this.getStockForScheme(row.scheme_name);
          const saved = this.getSavedLine(row, newDc);

          // Saved DC: show the current remaining stock of the dispatched lot.
          if (saved) {
            const matched = stocksForScheme.find(
              s => s.lot_no === saved.lot_no
            );

            return matched ? <span>{matched.quantity}</span> : "-";
          }

          const effective = this.getEffectiveLotFields(row, stocksForScheme);

          // Several lots and none picked yet
          if (stocksForScheme.length > 1 && !effective.lot_no) {
            return <span className="text-muted">Select lot first</span>;
          }

          // No stock for this scheme
          if (
            stocksForScheme.length === 0 ||
            effective.stock_quantity === null ||
            effective.stock_quantity === undefined
          ) {
            return "-";
          }

          return <span>{effective.stock_quantity}</span>;
        },
      },

      {
        dataField: "quantity",
        text: compact ? "Qty" : "Quantity",
        headerStyle: { width: w("14%", "10%"), textAlign: "center" },
        style: { textAlign: "center", padding: "4px" },
        formatter: (cell, row) => {
          // Saved DC → show the stored quantity.
          const saved = this.getSavedLine(row, newDc);

          if (saved) {
            return <span>{saved.quantity}</span>;
          }

          return (
            <>
              <Input
                bsSize="sm"
                type="number"
                min="0"
                className="d-print-none"
                value={this.state.schemeFieldsById[row.id]?.quantity ?? "1"}
                onChange={e =>
                  this.handleSchemeFieldChange(
                    row.id,
                    "quantity",
                    e.target.value
                  )
                }
              />
              <span className="print-only-value">
                {this.state.schemeFieldsById[row.id]?.quantity ?? "1"}
              </span>
            </>
          );
        },
      },
    ];

    return compact ? [dcNoColumn, dateColumn, ...baseColumns] : baseColumns;
  };

  // Read-only columns for ONE saved DC. DC No / Date are not columns here,
  // they live in that block's header.
  buildSavedColumns = () => [
    {
      dataField: "scheme_name",
      text: "Scheme Name",
      headerStyle: { width: "30%", textAlign: "left" },
      style: { textAlign: "left" },
    },
    {
      dataField: "rounds",
      text: "Round",
      headerStyle: { width: "14%", textAlign: "center" },
      style: { textAlign: "center" },
    },
    {
      dataField: "lot_no",
      text: "Lot No",
      headerStyle: { width: "16%", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: c => c || "-",
    },
    {
      dataField: "expiry_date",
      text: "Expiry Date",
      headerStyle: { width: "14%", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: c => formatDMY(c),
    },
    {
      dataField: "remaining_quantity",
      text: "Remaining Quantity",
      isDummyField: true,
      // Internal stock figure: shown on screen only, not printed.
      classes: "d-print-none",
      headerClasses: "d-print-none",
      headerStyle: { width: "14%", textAlign: "center" },
      style: { textAlign: "center" },
      formatter: (c, row) => {
        const m = this.getStockForScheme(row.scheme_name).find(
          s => s.lot_no === row.lot_no
        );

        return m ? <span>{m.quantity}</span> : "-";
      },
    },
    {
      dataField: "quantity",
      text: "Quantity",
      headerStyle: { width: "12%", textAlign: "center" },
      style: { textAlign: "center" },
    },
  ];

  // ✅ One COMPLETE challan page for one saved DC: logo + title + details +
  // address + scheme table + note + its own Print button. While one block is
  // being printed, all the others get d-print-none.
  renderSavedChallan = (group, paydate, participantPhone) => {
    const { participant, printDcNo } = this.state;
    const hidden = printDcNo && printDcNo !== group.dcNo;
    const label = { fontWeight: 600, minWidth: "110px" };

    return (
      <div
        key={group.dcNo}
        className={`challan-block mb-5 ${hidden ? "d-print-none" : ""}`}
      >
        <div className="print-logo">
          <img
            src="https://externalqcapi.com/media/organization/nhs_logo.webp"
            alt="Logo"
            style={{ height: "80px" }}
          />
        </div>

        <h3 className="challan-title">Delivery Challan</h3>

        <Row className="mb-4 challan-front-page">
          <Col md="6" className="mb-3 mb-md-0 challan-details-col">
            <div className="challan-section-heading">Details</div>

            <div className="d-flex align-items-center mb-2">
              <span style={label}>DC No</span>
              <span>{group.dcNo}</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <span style={label}>Date</span>
              <span>{formatDMY(group.date)}</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <span style={label}>Order No</span>
              <span>{buildOrderNo(paydate)}</span>
            </div>

            <div className="d-flex align-items-center mb-2">
              <span style={label}>Order Date</span>
              <span>{formatDMY(paydate)}</span>
            </div>
          </Col>

          <Col
            md="6"
            className="challan-address-col text-end"
            style={{ textAlign: "right" }}
          >
            <div className="challan-section-heading">Delivery Address</div>

            <div style={{ lineHeight: 1.8, textAlign: "right" }}>
              <div style={{ fontWeight: 700, fontSize: "20px" }}>
                {participant.lab_name || participant.name || "-"}
              </div>

              <div style={{ fontWeight: 400 }}>
                <span>Attention:</span>{" "}
                {!isEmptyVal(participant.lab_staff_name)
                  ? participant.lab_staff_name
                  : "-"}
              </div>

              <div>
                <span style={{ fontWeight: 700 }}>Contact:</span>{" "}
                <span style={{ fontWeight: 400 }}>
                  {!isEmptyVal(participantPhone) ? participantPhone : "-"}
                </span>
              </div>

              <div>
                {!isEmptyVal(participant.shipping_address)
                  ? participant.shipping_address
                  : "-"}
              </div>

              <div style={{ fontWeight: 700 }}>
                {!isEmptyVal(participant.city) ? participant.city : "-"}
              </div>

              <div style={{ fontWeight: 700 }}>
                {!isEmptyVal(participant.district) ? participant.district : "-"}
              </div>
            </div>
          </Col>
        </Row>

        <FormGroup>
          <BootstrapTable
            bootstrap4
            bordered
            keyField="id"
            classes="challan-scheme-table"
            data={group.rows}
            columns={this.buildSavedColumns()}
          />
        </FormGroup>

        <div className="challan-note">
          Note: This is an electronic document and does not require a stamp by
          NHS NEQAS.
        </div>

        <div className="mt-3 d-print-none">
          <Button
            color="primary"
            size="sm"
            onClick={() => this.handlePrintDc(group.dcNo)}
          >
            <i className="mdi mdi-printer me-1" />
            Print DC {group.dcNo}
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const { participant, dispatchDate, creatingDc, dcCreateMessage } =
      this.state;
    const {
      isRepeat,
      repeatCreated,
      historyLoading,
      historyError,
      showAllRounds,
      viewMode,
    } = this.state;

    // true on the Repeat page and in the "Pending Only" view
    const newDc = this.isNewDcMode();

    const dcNo = this.getCurrentDcNo();
    const dateTaken = isRepeat && !repeatCreated && this.isDateTaken();
    const visibleHistory = isRepeat ? this.getVisibleHistory() : [];

    // All Schemes view + at least one saved DC → saved data is read-only,
    // one separate challan block per DC No.
    const savedView = !newDc && this.state.history.length > 0;
    const savedGroups = savedView ? this.getSavedGroups() : [];

    // Schemes/rounds with no DC yet, and whether to show the toggle.
    const pendingSchemes = !isRepeat ? this.getPendingSchemes() : [];
    // ✅ The "Pending Only" / "Create DC for Pending Scheme(s)" option exists
    // ONLY when the lab already has a DC AND at least one open scheme
    // (one or more) still has no DC. If every scheme has a DC, it is hidden.
    const hasPending = !isRepeat && pendingSchemes.length > 0;
    const showToggle = hasPending && this.state.history.length > 0;

    // Date is editable on the Repeat page and in Pending Only view until created.
    const dateLocked = isRepeat && repeatCreated;

    const participantPhone = participant
      ? participant.landline_registered_by
      : "";

    // Single source for both Order No and Order Date.
    const paydate = getPayDate(participant) || this.state.paydate;

    // All Schemes / Pending Only toggle — shown when some schemes already
    // have a DC and others don't.
    const toggleAlert = showToggle && (
      <div
        className="alert alert-warning d-print-none d-flex justify-content-between align-items-center flex-wrap"
        style={{ gap: "8px" }}
      >
        <div>
          {pendingSchemes.length > 0 ? (
            <>
              <i className="mdi mdi-alert-outline me-1" />
              <b>{pendingSchemes.length}</b> scheme(s) still have no Delivery
              Challan:{" "}
              {pendingSchemes
                .map(s => `${s.scheme_name} (${s.rounds})`)
                .join(", ")}
            </>
          ) : (
            "All schemes now have a Delivery Challan."
          )}
        </div>

        <ButtonGroup size="sm">
          <Button
            color="primary"
            outline={viewMode !== "all"}
            onClick={() => this.handleViewModeChange("all")}
          >
            All Schemes
          </Button>
          <Button
            color="primary"
            outline={viewMode !== "pending"}
            onClick={() => this.handleViewModeChange("pending")}
          >
            Pending Only ({pendingSchemes.length})
          </Button>
        </ButtonGroup>
      </div>
    );

    return (
      <div className="page-content">
        <MetaTags>
          <h3>Delivery Challan | NEQAS</h3>
        </MetaTags>

        <style>{PRINT_STYLES}</style>

        <Container fluid>
          {/* Logo: hidden on screen, right-aligned at the top when printing.
              In the saved view every DC block has its own logo instead. */}
          {!savedView && (
            <div className="print-logo">
              <img
                src="https://externalqcapi.com/media/organization/nhs_logo.webp"
                alt="Logo"
                style={{ height: "80px" }}
              />
            </div>
          )}

          <Row className="justify-content-center">
            <Col lg="8">
              {isRepeat && (
                <Card className="d-print-none">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">
                        Step 1 — Existing DCs
                        {participant
                          ? ` — ${
                              participant.lab_name || participant.name || ""
                            }`
                          : ""}
                      </h5>
                    </div>

                    <Label>Already created DC(s)</Label>

                    <BootstrapTable
                      bootstrap4
                      bordered
                      keyField="_key"
                      data={visibleHistory}
                      columns={[
                        {
                          dataField: "dc_no",
                          text: "DC No",
                          headerStyle: { width: "13%", textAlign: "center" },
                          style: { textAlign: "center" },
                        },
                        {
                          dataField: "dispatch_date",
                          text: "Date",
                          headerStyle: { width: "15%", textAlign: "center" },
                          style: { textAlign: "center" },
                          formatter: cell => formatDMY(cell),
                        },
                        {
                          dataField: "scheme_name",
                          text: "Scheme Name",
                          headerStyle: { width: "26%", textAlign: "left" },
                          style: { textAlign: "left" },
                        },
                        {
                          dataField: "rounds",
                          text: "Round(s)",
                          headerStyle: { width: "11%", textAlign: "center" },
                          style: { textAlign: "center" },
                        },
                        {
                          dataField: "lot_no",
                          text: "Lot No",
                          headerStyle: { width: "15%", textAlign: "center" },
                          style: { textAlign: "center" },
                          formatter: cell => cell || "-",
                        },
                        {
                          dataField: "expiry_date",
                          text: "Expiry Date",
                          headerStyle: { width: "13%", textAlign: "center" },
                          style: { textAlign: "center" },
                          formatter: cell => formatDMY(cell),
                        },
                        {
                          dataField: "quantity",
                          text: "Qty",
                          headerStyle: { width: "7%", textAlign: "center" },
                          style: { textAlign: "center" },
                        },
                      ]}
                      noDataIndication={() => (
                        <div
                          className={`text-center py-2 ${
                            historyError ? "text-danger" : "text-muted"
                          }`}
                        >
                          {historyLoading
                            ? "Loading delivery challans..."
                            : historyError ||
                              "No delivery challan created yet for these round(s)."}
                        </div>
                      )}
                    />
                  </CardBody>
                </Card>
              )}

              <Card>
                <CardBody>
                  {isRepeat && (
                    <div className="d-print-none mb-3">
                      <h5 className="mb-1">Step 2 — Create New DC</h5>
                      <div className="text-muted" style={{ fontSize: "13px" }}>
                        1. Choose a new <b>Date</b> &nbsp;→&nbsp; 2. Select{" "}
                        <b>Lot</b> and enter <b>Quantity</b> &nbsp;→&nbsp; 3.
                        Click <b>Create DC</b>
                      </div>
                    </div>
                  )}

                  {!participant ? (
                    <div className="text-center text-muted py-4">
                      <i
                        className="mdi mdi-truck-delivery-outline"
                        style={{
                          fontSize: "32px",
                          display: "block",
                        }}
                      />
                      No participant selected. Go back to the Sample Dispatch
                      Report and choose a participant to generate a challan.
                    </div>
                  ) : (
                    <>
                      {savedView ? (
                        <>
                          {/* Toggle first, then ONE challan block per saved DC */}
                          {toggleAlert}

                          {savedGroups.map(g =>
                            this.renderSavedChallan(
                              g,
                              paydate,
                              participantPhone
                            )
                          )}
                        </>
                      ) : (
                        <>
                          {/* Page title: at the top, right below the logo */}
                          <h3 className="challan-title">Delivery Challan</h3>

                          <Row className="mb-4 challan-front-page">
                            {/* Details = 4 columns (no heading / no line) */}
                            <Col
                              md="6"
                              className="mb-3 mb-md-0 challan-details-col"
                            >
                              <div className="challan-section-heading">
                                Details
                              </div>

                              <div className="d-flex align-items-center mb-2">
                                <span
                                  style={{ fontWeight: 600, minWidth: "110px" }}
                                >
                                  DC No
                                </span>
                                <span>{dcNo || "-"}</span>
                              </div>

                              <div className="d-flex align-items-center mb-2">
                                <span
                                  style={{ fontWeight: 600, minWidth: "110px" }}
                                >
                                  Date
                                </span>
                                <div>
                                  {dateLocked ? (
                                    // Created Repeat DC: date is fixed text.
                                    <span>{formatDMY(dispatchDate)}</span>
                                  ) : (
                                    <>
                                      <DateField
                                        className="d-print-none"
                                        width="160px"
                                        value={dispatchDate}
                                        onChange={e =>
                                          this.setState({
                                            dispatchDate: e.target.value,
                                          })
                                        }
                                      />
                                      {dateTaken && (
                                        <div
                                          className="text-danger d-print-none"
                                          style={{ fontSize: "12px" }}
                                        >
                                          A DC already exists for this date.
                                          Choose another date.
                                        </div>
                                      )}
                                      <span className="print-only-value">
                                        {formatDMY(dispatchDate)}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <div className="d-flex align-items-center mb-2">
                                <span
                                  style={{ fontWeight: 600, minWidth: "110px" }}
                                >
                                  Order No
                                </span>
                                <span>{buildOrderNo(paydate)}</span>
                              </div>

                              <div className="d-flex align-items-center mb-2">
                                <span
                                  style={{ fontWeight: 600, minWidth: "110px" }}
                                >
                                  Order Date
                                </span>
                                <span>{formatDMY(paydate)}</span>
                              </div>
                            </Col>

                            {/* Customer details on the right, right-aligned */}
                            <Col
                              md="6"
                              className="challan-address-col text-end"
                              style={{ textAlign: "right" }}
                            >
                              <div className="challan-section-heading">
                                Delivery Address
                              </div>

                              <div
                                style={{ lineHeight: 1.8, textAlign: "right" }}
                              >
                                {/* Lab name: large + bold */}
                                <div
                                  style={{
                                    fontWeight: 700,
                                    fontSize: "20px",
                                  }}
                                >
                                  {participant.lab_name ||
                                    participant.name ||
                                    "-"}
                                </div>

                                {/* Attention */}
                                <div style={{ fontWeight: 400 }}>
                                  <span>Attention:</span>{" "}
                                  {!isEmptyVal(participant.lab_staff_name)
                                    ? participant.lab_staff_name
                                    : "-"}
                                </div>

                                {/* Contact */}
                                <div>
                                  <span style={{ fontWeight: 700 }}>
                                    Contact:
                                  </span>{" "}
                                  <span style={{ fontWeight: 400 }}>
                                    {!isEmptyVal(participantPhone)
                                      ? participantPhone
                                      : "-"}
                                  </span>
                                </div>

                                {/* Address */}
                                <div>
                                  {!isEmptyVal(participant.shipping_address)
                                    ? participant.shipping_address
                                    : "-"}
                                </div>

                                {/* City: bold */}
                                <div style={{ fontWeight: 700 }}>
                                  {!isEmptyVal(participant.city)
                                    ? participant.city
                                    : "-"}
                                </div>

                                {/* District: bold */}
                                <div style={{ fontWeight: 700 }}>
                                  {!isEmptyVal(participant.district)
                                    ? participant.district
                                    : "-"}
                                </div>
                              </div>
                            </Col>
                          </Row>

                          {toggleAlert}

                          {/* Pending Only: saved DCs stay visible in their own
                              read-only table (not printed). */}
                          {!isRepeat && viewMode === "pending" && (
                            <div className="d-print-none mb-4">
                              <h5 className="mb-2">Already created DC(s)</h5>
                              <BootstrapTable
                                bootstrap4
                                bordered
                                keyField="id"
                                classes="challan-scheme-table"
                                data={this.getSavedSchemes()}
                                columns={this.buildSchemeColumns(false)}
                                noDataIndication={() => (
                                  <div className="text-muted text-center py-2">
                                    No saved DC yet.
                                  </div>
                                )}
                              />
                            </div>
                          )}

                          {!isRepeat && viewMode === "pending" && (
                            <div className="d-print-none mb-2">
                              <h5 className="mb-1">
                                New DC — Pending Scheme(s) only
                              </h5>
                              <div
                                className="text-muted"
                                style={{ fontSize: "13px" }}
                              >
                                Select Lot, enter Quantity, then click{" "}
                                <b>Create DC for Pending Scheme(s)</b>. A new,
                                unique DC No ({dcNo || "-"}) is issued for these
                                scheme(s) only.
                              </div>
                            </div>
                          )}

                          <FormGroup>
                            <BootstrapTable
                              bootstrap4
                              bordered
                              keyField="id"
                              classes="challan-scheme-table"
                              data={this.getDisplayedSchemes()}
                              columns={this.buildSchemeColumns(newDc)}
                              noDataIndication={() => (
                                <div className="text-muted text-center py-2">
                                  {viewMode === "pending"
                                    ? "No pending schemes - every open scheme already has a Delivery Challan."
                                    : "No open rounds for this participant."}
                                </div>
                              )}
                            />
                          </FormGroup>
                        </>
                      )}

                      {dcCreateMessage && (
                        <div
                          className={`alert d-print-none ${
                            dcCreateMessage.includes("successfully")
                              ? "alert-success"
                              : "alert-danger"
                          } text-center`}
                        >
                          {dcCreateMessage}
                        </div>
                      )}

                      {/* Note at the end of the page (shown on screen and in
                          print). In the saved view each block has its own. */}
                      {!savedView && (
                        <div className="challan-note">
                          Note: This is an electronic document and does not
                          require a stamp by NHS NEQAS.
                        </div>
                      )}
                    </>
                  )}

                  <div className="mt-4 d-flex gap-2 d-print-none flex-wrap">
                    {participant && (
                      <>
                        <Button color="primary" onClick={this.handlePrint}>
                          <i className="mdi mdi-printer me-1" />
                          {savedView && savedGroups.length > 1
                            ? "Print All (separate pages)"
                            : "Print Challan"}
                        </Button>

                        {(newDc ? repeatCreated : this.state.dcCreated) ? (
                          <>
                            <span className="badge bg-success align-self-center px-3 py-2">
                              <i className="mdi mdi-check-circle-outline me-1" />
                              DC Created
                            </span>

                            {isRepeat && (
                              <Button
                                color="secondary"
                                outline
                                onClick={this.handleStartAnotherDc}
                              >
                                <i className="mdi mdi-plus-circle-outline me-1" />
                                Create Another DC
                              </Button>
                            )}

                            {/* DC exists, but some scheme(s) still have no challan */}
                            {!isRepeat && !newDc && pendingSchemes.length > 0 && (
                              <Button
                                color="warning"
                                onClick={() =>
                                  this.handleViewModeChange("pending")
                                }
                              >
                                <i className="mdi mdi-file-document-plus-outline me-1" />
                                Create DC for Pending Scheme(s) (
                                {pendingSchemes.length})
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            {/* In pending view the button only exists while
                                at least one scheme is pending. */}
                            {(isRepeat ||
                              viewMode !== "pending" ||
                              hasPending) && (
                              <Button
                                color="success"
                                disabled={creatingDc || dateTaken}
                                onClick={this.handleCreateDc}
                              >
                                <i className="mdi mdi-file-document-check-outline me-1" />
                                {creatingDc
                                  ? "Creating..."
                                  : !isRepeat && viewMode === "pending"
                                  ? "Create DC for Pending Scheme(s)"
                                  : "Create DC"}
                              </Button>
                            )}

                            {!isRepeat && viewMode === "pending" && (
                              <Button
                                color="secondary"
                                outline
                                onClick={() => this.handleViewModeChange("all")}
                              >
                                Back to All Schemes
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

DeliveryChallan.propTypes = {
  SampleReport: PropTypes.array,
  StockList: PropTypes.array,

  onGetSampleReport: PropTypes.func,
  onGetStock: PropTypes.func,

  location: PropTypes.shape({
    state: PropTypes.object,
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,

  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    createHref: PropTypes.func,
  }).isRequired,

  match: PropTypes.shape({
    params: PropTypes.shape({
      organization_name: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

// StockList lives under the same "SchemeList" reducer slice as SchemeList
// itself (see the Stock In page's mapStateToProps).
const mapStateToProps = state => ({
  SampleReport: state.ListUnit.SampleReport,
  StockList: state.SchemeList.StockList,
});

const mapDispatchToProps = dispatch => ({
  onGetSampleReport: id => dispatch(sampleReport(id)),
  onGetStock: id => dispatch(getStockList(id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(DeliveryChallan));